import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import React, { useEffect } from "react";
import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import FormInputField from "@/components/lib/from-input-field";
import { useForm } from "react-hook-form";
import { Form } from "@/components/ui/form";
import { Separator } from "@/components/ui/separator";
import { Button } from "@/components/ui/button";
import { useState } from "react";
import { blackUser } from "@/assets";
import { toast } from "react-hot-toast";
import { useSelector, useDispatch } from "react-redux";
import moment from "moment";
import { CheckCircle, XCircle } from "lucide-react";
import { DeleteConfirmDialog } from "@/components/dialog/common-dialog";
import {
  updateLeave,
  approveLeave,
  rejectLeave,
} from "@/redux/slice/staff-management/staff-leave-slice";

export function LeavesTable({ leaves }) {
  const dispatch = useDispatch();
  const [openLeavesDialog, setLeavesDialog] = useState(false);
  const [itemDate, setItemData] = useState(null);
  const [confirmDialog, setConfirmDialog] = useState({
    isOpen: false,
    action: null,
    item: null,
  });

  const { approveLoading, rejectLoading } = useSelector(
    (state) => state.staffManage.leaves
  );

  const onDialog = () => {
    setLeavesDialog(true);
  };

  const offDialog = () => {
    setLeavesDialog(false);
    setItemData(null);
  };

  const handleConfirmStatusChange = async () => {
    const { action, item } = confirmDialog;
    if (!item?.id || !action) return;

    let res;
    if (action === "Approved") {
      res = await dispatch(approveLeave(item.id));
    } else if (action === "Rejected") {
      res = await dispatch(rejectLeave(item.id));
    }

    if (approveLeave.fulfilled.match(res) || rejectLeave.fulfilled.match(res)) {
      toast.success(
        `${item.name}'s leave ${action.toLowerCase()} successfully!`
      );
      setConfirmDialog({ isOpen: false, action: null, item: null });
    } else if (
      approveLeave.rejected.match(res) ||
      rejectLeave.rejected.match(res)
    ) {
      toast.error(res.payload || `Failed to ${action.toLowerCase()} leave!`);
    }
  };

  return (
    <>
      <Table className="w-full">
        <TableHeader>
          <TableRow>
            <TableHead className="w-[100px]">ID</TableHead>
            <TableHead>Name</TableHead>
            <TableHead>Designations</TableHead>
            <TableHead>From</TableHead>
            <TableHead>To</TableHead>
            <TableHead>Days</TableHead>
            <TableHead>Note</TableHead>
            <TableHead>Status</TableHead>
            <TableHead className="text-right pr-4">Action</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {leaves && leaves.length > 0 ? (
            leaves?.map((item, index) => {
              const isPending =
                !item?.status || item?.status?.toLowerCase() === "pending";
              const isApproved = item?.status?.toLowerCase() === "approved";
              const isRejected = item?.status?.toLowerCase() === "rejected";

              return (
                <TableRow key={index}>
                  <TableCell className="font-medium">
                    {item.staff_id || "N/A"}
                  </TableCell>
                  <TableCell>
                    <div className="flex items-center gap-2">
                      <img
                        src={item?.file_manager_url || blackUser}
                        alt={item?.name}
                        className="w-8 h-8 rounded-full"
                      />
                      <span>{item?.name || "N/A"}</span>
                    </div>
                  </TableCell>
                  <TableCell>{item?.designation || "N/A"}</TableCell>
                  <TableCell>{item?.form_date || "N/A"}</TableCell>
                  <TableCell>{item?.to_date || "N/A"}</TableCell>
                  <TableCell>{item?.days || "N/A"}</TableCell>
                  <TableCell>{item?.notes || item?.reason || "N/A"}</TableCell>
                  <TableCell>
                    {isPending ? (
                      <Select
                        value=""
                        onValueChange={(value) => {
                          if (!value) return;
                          setConfirmDialog({
                            isOpen: true,
                            action: value,
                            item: item,
                          });
                        }}
                      >
                        <SelectTrigger className="w-[120px] h-8 border-neutral-700 bg-cblack3 text-cgreen">
                          <SelectValue placeholder="Pending" />
                        </SelectTrigger>
                        <SelectContent className="bg-cblack4 text-white border-neutral-700">
                          <SelectGroup>
                            <SelectItem value="Approved" className="text-cgreen font-medium">
                              Approve
                            </SelectItem>
                            <SelectItem value="Rejected" className="text-cred font-medium">
                              Reject
                            </SelectItem>
                          </SelectGroup>
                        </SelectContent>
                      </Select>
                    ) : (
                      <span
                        className={`inline-flex items-center px-2.5 py-0.5 rounded text-xs font-medium capitalize ${
                          isApproved
                            ? "bg-emerald-500/10 text-emerald-400 border border-emerald-500/30"
                            : "bg-red-500/10 text-red-400 border border-red-500/30"
                        }`}
                      >
                        {item?.status}
                      </span>
                    )}
                  </TableCell>
                  <TableCell className="text-right pr-4">
                    {isPending ? (
                      <span
                        onClick={() => {
                          setLeavesDialog(true);
                          setItemData(item);
                        }}
                        className="cursor-pointer text-cyellow hover:underline font-medium"
                      >
                        Edit
                      </span>
                    ) : (
                      <span className="text-neutral-500 text-sm">-</span>
                    )}
                  </TableCell>
                </TableRow>
              );
            })
          ) : (
            <TableRow>
              <TableCell colSpan={10} className="text-center">
                No data available
              </TableCell>
            </TableRow>
          )}
        </TableBody>
      </Table>

      <LeavesDialog
        itemDate={itemDate}
        openLeavesDialog={openLeavesDialog}
        offDialog={offDialog}
      />

      <DeleteConfirmDialog
        isOpen={confirmDialog.isOpen}
        onClose={() =>
          setConfirmDialog({ isOpen: false, action: null, item: null })
        }
        onConfirm={handleConfirmStatusChange}
        icon={
          confirmDialog.action === "Approved" ? (
            <CheckCircle className="text-cgreen w-6 h-6" />
          ) : (
            <XCircle className="text-cred w-6 h-6" />
          )
        }
        title={
          confirmDialog.action === "Approved"
            ? "Approve Leave Request"
            : "Reject Leave Request"
        }
        description={`Are you sure you want to ${
          confirmDialog.action === "Approved" ? "approve" : "reject"
        } ${confirmDialog.item?.name ? `${confirmDialog.item.name}'s` : "this"} leave request?`}
        yesBtnTxt={
          confirmDialog.action === "Approved" ? "Yes, Approve" : "Yes, Reject"
        }
        yesBtnClass={
          confirmDialog.action === "Approved"
            ? "bg-cgreen hover:bg-green-600 text-black font-semibold"
            : "bg-cred hover:bg-red-700 text-white"
        }
        isLoading={approveLoading || rejectLoading}
        autoCloseOnConfirm={false}
      />
    </>
  );
}

export default LeavesTable;

export function LeavesDialog({ openLeavesDialog, offDialog, itemDate }) {
  const dispatch = useDispatch();
  const form = useForm({
    resolver: zodResolver(leavesSchema),
    defaultValues: {
      from: "",
      to: "",
      reason: "",
    },
  });
  const { updateLoading } = useSelector((state) => state.staffManage.leaves);

  useEffect(() => {
    if (itemDate) {
      const fromVal = itemDate?.form_date || itemDate?.from_date;
      const toVal = itemDate?.to_date;
      form.reset({
        from: fromVal ? new Date(fromVal) : "",
        to: toVal ? new Date(toVal) : "",
        reason: itemDate?.reason || itemDate?.notes || itemDate?.note || "",
      });
    }
  }, [itemDate, form]);

  const editLeave = async (data) => {
    const fromDateStr = moment(data.from).format("YYYY-MM-DD");
    const toDateStr = moment(data.to).format("YYYY-MM-DD");

    if (moment(toDateStr).isBefore(moment(fromDateStr))) {
      toast.error("To date must be the same or after From date!");
      return;
    }

    const days = moment(toDateStr).diff(moment(fromDateStr), "days") + 1;

    const payload = {
      form_date: fromDateStr,
      to_date: toDateStr,
      days: days,
      reason: data.reason || "",
    };

    const res = await dispatch(
      updateLeave({ leave_id: itemDate?.id, data: payload })
    );
    if (updateLeave.fulfilled.match(res)) {
      toast.success("Leave updated successfully!");
      offDialog();
    } else if (updateLeave.rejected.match(res)) {
      toast.error(res.payload || "Failed to update leave!");
    }
  };

  return (
    <Dialog open={openLeavesDialog} onOpenChange={offDialog}>
      <DialogContent className="sm:max-w-[800px]">
        <div className="py-5 flex flex-col">
          <DialogTitle className="text-lg absolute top-4 font-medium">
            Edit Leaves : {itemDate?.staff_id || "N/A"}
          </DialogTitle>
          <Separator className="mt-4" />
          <Form {...form}>
            <form
              onSubmit={form.handleSubmit(editLeave)}
              className="flex flex-col gap-8 mt-6"
            >
              <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                <FormInputField
                  name="from"
                  style=""
                  placeholder={"Select Date"}
                  form={form}
                  type="date"
                  label={"From"}
                />

                <FormInputField
                  name="to"
                  placeholder={"Select Date"}
                  form={form}
                  type="date"
                  label={"To"}
                />
              </div>

              <FormInputField
                name="reason"
                style=" h-36"
                placeholder={"Write Note"}
                form={form}
                type="textarea"
                label={"Note"}
              />

              <Button
                isLoading={updateLoading}
                className="bg-cyellow w-full mt-5 text-cblack"
              >
                Confirm
              </Button>
            </form>
          </Form>
        </div>
      </DialogContent>
    </Dialog>
  );
}

const leavesSchema = z.object({
  from: z.preprocess(
    (val) => (val ? new Date(val) : val),
    z.date({ required_error: "From date is required" })
  ),
  to: z.preprocess(
    (val) => (val ? new Date(val) : val),
    z.date({ required_error: "To date is required" })
  ),
  reason: z.string().min(1, "The reason field is required."),
});
